/* eslint-disable no-param-reassign */

class Breadcrumbs extends HTMLElement {
  connectedCallback() {
    this.breadcrumbContainer = this.querySelector(".breadcrumbs__container");
    if (!this.breadcrumbContainer) {
      console.error("Breadcrumbs container not found");
      return;
    }
    this.generateBreadcrumbsFromMenu();
  }

  static isFrenchWithCHF() {
    // Check for French locale and CHF currency
    const locale = window.Shopify?.locale;
    const currency = window.Shopify?.currency?.active;
    const result = locale?.startsWith("fr") && currency === "CHF";
    return result;
  }

  static getMenuData() {
    // Check if French locale and CHF currency
    const isFrenchWithCHF = Breadcrumbs.isFrenchWithCHF();

    // Get appropriate menu data
    if (isFrenchWithCHF && window.menu_json_fr) {
      return window.menu_json_fr;
    }

    return window.menu_json;
  }

  generateBreadcrumbsFromMenu() {
    const menuData = Breadcrumbs.getMenuData();
    if (!menuData || !menuData.menuItems) {
      return;
    }

    // Strip locale prefix from path for matching (e.g., /de/collections/... -> /collections/...)
    const stripLocalePrefix = (path) => path.replace(/^\/(de|fr)\//, "/");

    const currentPath = stripLocalePrefix(window.location.pathname);
    const currentUrl = window.location.origin + currentPath;

    // Check if we're on a product page
    const isProductPage = currentPath.includes("/products/");

    let breadcrumbs = [];

    if (isProductPage) {
      // For product pages, find breadcrumbs based on product collections
      breadcrumbs = this.findProductBreadcrumbs(menuData.menuItems);
    }

    // If no product breadcrumbs found or not a product page, try regular path matching
    if (breadcrumbs.length === 0) {
      breadcrumbs = this.findBreadcrumbPath(menuData.menuItems, currentPath);
      if (breadcrumbs.length === 0) {
        breadcrumbs = this.findBreadcrumbPath(menuData.menuItems, currentUrl);
      }
    }

    if (breadcrumbs.length === 0) {
      return;
    }

    this.renderBreadcrumbs(breadcrumbs);
  }

  findProductBreadcrumbs(menuItems) {
    // Get product collections from Shopify global object or product meta
    const productCollections = Breadcrumbs.getProductCollections();

    if (!productCollections || productCollections.length === 0) {
      return [];
    }

    // Try to find a collection that exists in our menu structure
    // Priority: find the most specific (deepest) collection match
    let bestMatch = [];
    let bestDepth = 0;

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < productCollections.length; i++) {
      const collection = productCollections[i];
      const collectionUrl = `/collections/${collection.handle}`;

      // Find this collection in the menu structure by handle URL
      let breadcrumbs = this.findBreadcrumbPath(menuItems, collectionUrl);

      // If not found by handle, try to find by collection ID
      if (breadcrumbs.length === 0 && collection.id) {
        breadcrumbs = this.findBreadcrumbPathById(menuItems, collection.id);
      }

      // Prefer the match with the greatest depth (most specific)
      if (breadcrumbs.length > bestDepth) {
        bestMatch = breadcrumbs;
        bestDepth = breadcrumbs.length;
      }
    }

    return bestMatch;
  }

  static getProductCollections() {
    // Get collections from window.product_collections (array of collection objects)
    if (window.product_collections && Array.isArray(window.product_collections)) {
      return window.product_collections;
    }

    // Single collection object
    if (window.product_collections && window.product_collections.handle) {
      return [window.product_collections];
    }

    return [];
  }

  findBreadcrumbPath(menuItems, targetUrl, parentPath = []) {
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < menuItems.length; i++) {
      const item = menuItems[i];
      const currentPath = [...parentPath, { title: item.title, url: item.url }];

      // Normalize URLs for comparison (remove parameters and trailing slashes)
      const normalizeUrl = (url) => {
        try {
          const urlObj = new URL(url, window.location.origin);
          return urlObj.pathname.replace(/\/$/, "") || "/";
        } catch {
          // If URL parsing fails, treat as pathname
          return url.split("?")[0].replace(/\/$/, "") || "/";
        }
      };

      const normalizedItemUrl = normalizeUrl(item.url);
      const normalizedTargetUrl = normalizeUrl(targetUrl);

      // Check if current item matches the target URL
      if (normalizedItemUrl === normalizedTargetUrl || item.url === targetUrl) {
        return currentPath;
      }

      // Check submenus recursively
      if (item.subMenu && item.subMenu.length > 0) {
        const subPath = this.findBreadcrumbPath(item.subMenu, targetUrl, currentPath);
        if (subPath.length > 0) {
          return subPath;
        }
      }
    }

    return [];
  }

  findBreadcrumbPathById(menuItems, targetId, parentPath = []) {
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < menuItems.length; i++) {
      const item = menuItems[i];
      const currentPath = [...parentPath, { title: item.title, url: item.url }];

      // Check if current item's ID matches the target collection ID
      // Menu items might have their ID stored in different properties
      if (item.collectionId && String(item.collectionId) === String(targetId)) {
        return currentPath;
      }

      // Check submenus recursively
      if (item.subMenu && item.subMenu.length > 0) {
        const subPath = this.findBreadcrumbPathById(item.subMenu, targetId, currentPath);
        if (subPath.length > 0) {
          return subPath;
        }
      }
    }

    return [];
  }

  renderBreadcrumbs(breadcrumbs) {
    // Clear existing breadcrumbs
    this.breadcrumbContainer.innerHTML = "";

    const svg = `
      <svg
        width="16"
        height="16"
        aria-hidden="false"
        aria-label="Arrow right breadcrumb"
        class="icon breadcrumbs__breadcrumb-icon"
        style="--icon-fill: none; --icon-stroke: var(--text-color--secondary);"
      >
        <use xlink:href="#icon-arrow-right"></use>
      </svg>
    `;

    breadcrumbs.forEach((crumb, index) => {
      const isLast = index === breadcrumbs.length - 1;

      if (isLast) {

        const stripLocalePrefix = (path) => path.replace(/^\/(de|fr)\//, "/");
        const currentPath = stripLocalePrefix(window.location.pathname);
        const currentUrl = window.location.origin + currentPath;

        // Check if we're on a product page
        const isProductPage = currentPath.includes("/products/");
        if(isProductPage){
          const link = document.createElement("a");
          link.href = crumb.url;
          link.textContent = crumb.title;
          link.className =
            "text text--xx-sm text--regular text--line-height-md text--body-font text--underline-decoration breadcrumbs__breadcrumb-title";
          this.breadcrumbContainer.appendChild(link); 
        }else{
          // Last breadcrumb (current page) - render as span
          const span = document.createElement("span");
          span.textContent = crumb.title;
          span.className =
            "text text--xx-sm text--regular text--line-height-md text--body-font breadcrumbs__last-breadcrumb";
          this.breadcrumbContainer.appendChild(span); 
        }      
      } else {
        // Regular breadcrumb - render as link
        const link = document.createElement("a");
        link.href = crumb.url;
        link.textContent = crumb.title;
        link.className =
          "text text--xx-sm text--regular text--line-height-md text--body-font text--underline-decoration breadcrumbs__breadcrumb-title";
        this.breadcrumbContainer.appendChild(link);

        // Add separator arrow
        const svgNode = new DOMParser().parseFromString(svg, "text/html").querySelector("svg");
        this.breadcrumbContainer.appendChild(svgNode);
      }
    });
  }
}

if (!customElements.get("bread-crumbs")) {
  customElements.define("bread-crumbs", Breadcrumbs);
}
